if (!customElements.get("carousel-slider")) {
  customElements.define(
    "carousel-slider",
    class CarouselSlider extends HTMLElement {
      constructor() {
        super();
      }

      connectedCallback() {
        this.carousel = this.querySelector('[class*="grid-carousel"]');
        this.initialize();
      }

      initialize() {
        const autoplayEnabled = this.getAttribute("data-autoplay") === "true";
        const autoPlaySpeed = parseInt(this.getAttribute("data-speed"), 10) * 1000;
        const groupCells = this.getAttribute("data-group-cells") !== "false";
        const wrapAround = this.getAttribute("data-wrap-around") === "true";

        // Flickity doesn't work with flex containers, so
        // to avoid flash of unstyled content and page jump, style the container
        // as block just before initialization. On mobile where the carousel
        // is converted to simple scroller we swap this to display: flex !important
        // using CSS.
        this.carousel.style.display = "block";
        this.flickity = new Flickity(this.carousel, {
          autoPlay: autoplayEnabled ? autoPlaySpeed : false,
          cellAlign: "left",
          groupCells: groupCells,
          contain: true,
          resize: true,
          draggable: true,
          prevNextButtons: false,
          fade: false,
          cellSelector: ".grid-item",
          initialIndex: 0,
          pageDots: false,
          wrapAround: wrapAround,
          accessibility: false,
          watchCSS: true,
        });

        this.flickity.on("change", (index) => {
          publish(PUB_SUB_EVENTS.sliderSlideChange, {
            id: this.getAttribute("id"),
            index,
          });
        });

        requestAnimationFrame(() => {
          this.dispatchEvent(
            new CustomEvent("carousel:ready", {
              bubbles: true,
              detail: {
                id: this.id,
                element: this,
                flickity: this.flickity,
              },
            }),
          );
        });
      }
    },
  );
}

if (!customElements.get("carousel-navigation")) {
  customElements.define(
    "carousel-navigation",
    class CarouselNavigation extends HTMLElement {
      constructor() {
        super();
      }

      connectedCallback() {
        this.targetId = this.getAttribute("for");
        this.buttonPrev = this.querySelector(".js-prev");
        this.buttonNext = this.querySelector(".js-next");

        this.onPrevClick = (event) => {
          event.preventDefault();
          this.flickity?.previous();
        };

        this.onNextClick = (event) => {
          event.preventDefault();
          this.flickity?.next();
        };

        this.onCarouselReady = (event) => {
          if (event.detail.id !== this.targetId) {
            return;
          }

          this.attachToCarousel(event.detail.element, event.detail.flickity);
        };

        this.buttonPrev?.addEventListener("click", this.onPrevClick);
        this.buttonNext?.addEventListener("click", this.onNextClick);
        document.addEventListener("carousel:ready", this.onCarouselReady);

        this.attachToExistingCarousel();
      }

      disconnectedCallback() {
        this.buttonPrev?.removeEventListener("click", this.onPrevClick);
        this.buttonNext?.removeEventListener("click", this.onNextClick);
        document.removeEventListener("carousel:ready", this.onCarouselReady);
        this.detachFromCarousel();
      }

      attachToExistingCarousel() {
        const carouselComponent = document.querySelector(`#${this.targetId}`);
        if (!carouselComponent?.flickity) {
          return;
        }

        this.attachToCarousel(carouselComponent, carouselComponent.flickity);
      }

      attachToCarousel(carouselComponent, flickity) {
        if (!carouselComponent || !flickity || this.flickity === flickity) {
          return;
        }

        this.detachFromCarousel();

        this.carouselComponent = carouselComponent;
        this.flickity = flickity;

        this.syncButtons = () => this.updateButtons();
        this.flickity.on("select", this.syncButtons);
        this.flickity.on("settle", this.syncButtons);
        this.updateButtons();
      }

      updateButtons() {
        if (!this.flickity || !this.buttonPrev || !this.buttonNext) {
          return;
        }

        if (this.flickity.options.wrapAround) {
          return;
        }

        this.enableButton(this.buttonPrev);
        this.enableButton(this.buttonNext);

        const slides = this.flickity.slides;
        if (!slides || slides.length === 0) {
          return;
        }

        const current = slides[this.flickity.selectedIndex];
        const first = slides[0];
        const last = slides[slides.length - 1];

        if (current.target === first.target) {
          this.disableButton(this.buttonPrev);
        }
        if (current.target === last.target) {
          this.disableButton(this.buttonNext);
        }
      }

      detachFromCarousel() {
        if (!this.flickity) {
          return;
        }

        this.flickity = null;
        this.carouselComponent = null;
      }

      enableButton(button) {
        if (!button) return;
        button.classList.remove("pill-nav-item-disabled");
        button.classList.remove("focus-none");
        button.setAttribute("aria-disabled", "false");
      }

      disableButton(button) {
        if (!button) return;
        button.classList.add("pill-nav-item-disabled");
        button.classList.add("focus-none");
        button.setAttribute("aria-disabled", "true");
      }
    },
  );
}
